/**
 * Variance Bin Processing
 *
 * Handles spatial bin GPS interpolation for the consistency gradient.
 */

import { type Lap } from '../../../domain/lap/Lap'
import { SAMPLE_INTERVAL } from '../../config/chartDisplay'

// ============================================================================
// Types
// ============================================================================

export interface VarianceBin {
  distance: number
  lat: number
  lng: number
  values: number[][] // One array per metric
}

// ============================================================================
// GPS Coordinate Interpolation
// ============================================================================

/** Try to fill empty bins with GPS coordinates from lap GPS points. */
function fillBinsFromLapGps(
  bins: VarianceBin[],
  filteredLaps: Lap[],
  startDist: number
): void {
  const numBins = bins.length

  for (const lap of filteredLaps) {
    const gpsPoints = lap.getGpsPoints()
    if (gpsPoints.length === 0) continue

    for (const gpsPoint of gpsPoints) {
      const binIdx = Math.floor(
        (gpsPoint.distanceMeters - startDist) / SAMPLE_INTERVAL
      )
      if (binIdx < 0 || binIdx >= numBins) continue

      if (bins[binIdx].lat === 0 && bins[binIdx].lng === 0) {
        bins[binIdx].lat = gpsPoint.latitude
        bins[binIdx].lng = gpsPoint.longitude
      }
    }

    if (bins.some((b) => b.lat !== 0 || b.lng !== 0)) break
  }
}

/** Find the nearest bin with GPS coordinates searching backwards/forwards. */
function findNeighborGpsIdx(
  bins: VarianceBin[],
  startIdx: number,
  direction: -1 | 1
): number {
  for (
    let j = startIdx + direction;
    j >= 0 && j < bins.length;
    j += direction
  ) {
    if (bins[j].lat !== 0 || bins[j].lng !== 0) return j
  }
  return -1
}

/** Interpolate GPS coordinates for bins that lack them. */
function interpolateGpsGaps(bins: VarianceBin[]): void {
  for (let i = 0; i < bins.length; i++) {
    if (bins[i].lat !== 0 || bins[i].lng !== 0) continue

    const prevGpsIdx = findNeighborGpsIdx(bins, i, -1)
    const nextGpsIdx = findNeighborGpsIdx(bins, i, 1)

    if (prevGpsIdx >= 0 && nextGpsIdx >= 0) {
      const t = (i - prevGpsIdx) / (nextGpsIdx - prevGpsIdx)
      bins[i].lat =
        bins[prevGpsIdx].lat + t * (bins[nextGpsIdx].lat - bins[prevGpsIdx].lat)
      bins[i].lng =
        bins[prevGpsIdx].lng + t * (bins[nextGpsIdx].lng - bins[prevGpsIdx].lng)
    } else if (prevGpsIdx >= 0) {
      bins[i].lat = bins[prevGpsIdx].lat
      bins[i].lng = bins[prevGpsIdx].lng
    } else if (nextGpsIdx >= 0) {
      bins[i].lat = bins[nextGpsIdx].lat
      bins[i].lng = bins[nextGpsIdx].lng
    }
  }
}

/**
 * Ensure all bins have GPS coordinates by:
 * 1. Trying to fill from another lap's GPS points if no bins have coords
 * 2. Interpolating gaps between bins that do have coords
 */
export function ensureBinGpsCoordinates(
  bins: VarianceBin[],
  filteredLaps: Lap[],
  startDist: number
): void {
  const hasGps = bins.some((b) => b.lat !== 0 || b.lng !== 0)
  if (!hasGps) {
    fillBinsFromLapGps(bins, filteredLaps, startDist)
  }

  if (!bins.some((b) => b.lat !== 0 || b.lng !== 0)) return

  interpolateGpsGaps(bins)
}

/**
 * Trace-frame variant of `ensureBinGpsCoordinates` (pass 29): fills bin GPS
 * from SpeedTrace points, whose `distance` sits on the same recalibrated
 * frame the bins are indexed in — so the gradient's coordinates line up with
 * the recalibrated bin positions instead of raw odometer ones.
 */
export function ensureBinGpsFromTraces(
  bins: VarianceBin[],
  traces: { points: { distance: number; lat?: number; lng?: number }[] }[],
  startDist: number
): void {
  const numBins = bins.length
  if (!bins.some((b) => b.lat !== 0 || b.lng !== 0)) {
    for (const trace of traces) {
      for (const p of trace.points) {
        if (p.lat == null || p.lng == null || (p.lat === 0 && p.lng === 0))
          continue
        const binIdx = Math.floor((p.distance - startDist) / SAMPLE_INTERVAL)
        if (binIdx < 0 || binIdx >= numBins) continue
        if (bins[binIdx].lat === 0 && bins[binIdx].lng === 0) {
          bins[binIdx].lat = p.lat
          bins[binIdx].lng = p.lng
        }
      }
      if (bins.some((b) => b.lat !== 0 || b.lng !== 0)) break
    }
  }

  if (!bins.some((b) => b.lat !== 0 || b.lng !== 0)) return

  interpolateGpsGaps(bins)
}
